import { CircularProgress, TextField } from '@mui/material';
import { VirtualizedAutocomplete } from 'components/FeaturesDashboard/KeySelect/VirtualizedAutocomplete';
import { NamedFeature } from 'interfaces/features';
import { filter, find, includes, map, some, toLower, trim } from 'lodash';
import React from 'react';

export const VirtualizedFeaturesSelect: React.FC<
  React.PropsWithChildren<{
    featuresData: NamedFeature[];
    isLoadingFeaturesData: boolean;
    value: string;
    onChange: (e: any, featureKey: string | null) => void;
    label?: string;
  }>
> = ({ featuresData, isLoadingFeaturesData, value, onChange, label = 'column' }) => {
  const currentFeature = find(featuresData, { key: value });

  return (
    <VirtualizedAutocomplete<NamedFeature>
      disabled={isLoadingFeaturesData}
      size="small"
      options={featuresData}
      value={currentFeature}
      onChange={(e, feature) => {
        onChange(e, feature?.key);
      }}
      getOptionLabel={(feature) => feature?.nameOverride || feature?.displayName}
      renderOption={(option) => ({
        key: option?.key,
        displayName: option?.nameOverride || option?.displayName,
      })}
      filterOptions={(options, state) => {
        const inputValue = toLower(trim(state.inputValue));
        return filter(options, (option) =>
          some([option?.nameOverride, option?.displayName, option?.key], (field) =>
            includes(toLower(field || ''), inputValue)
          )
        );
      }}
      renderInput={(params) => (
        <TextField
          required
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoadingFeaturesData ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export const VirtualizedFeaturesMultiSelect: React.FC<
  React.PropsWithChildren<{
    featuresData: NamedFeature[];
    isLoadingFeaturesData: boolean;
    value: string[];
    onChange: (e: any, featureKey: string[] | []) => void;
  }>
> = ({ featuresData, isLoadingFeaturesData, value, onChange }) => {
  const currentFeature = filter(featuresData, (feature) => includes(value, feature.key));

  return (
    <VirtualizedAutocomplete<NamedFeature, false, false, true>
      multiple={true}
      disabled={isLoadingFeaturesData}
      size="small"
      options={featuresData}
      value={currentFeature}
      onChange={(e, features) => {
        onChange(
          e,
          map(features, (feature) => feature.key)
        );
      }}
      getOptionLabel={(feature) => feature?.nameOverride || feature?.displayName || ''}
      renderOption={(option) => ({
        key: option?.key,
        displayName: option?.nameOverride || option?.displayName,
      })}
      filterOptions={(options, state) => {
        const inputValue = toLower(trim(state.inputValue));
        return filter(options, (option) =>
          some([option?.nameOverride, option?.displayName, option?.key], (field) =>
            includes(toLower(field || ''), inputValue)
          )
        );
      }}
      renderInput={(params) => (
        <TextField
          required
          {...params}
          label="columns"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoadingFeaturesData ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};
